import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { toast } from 'sonner'
import { 
  Bot, 
  Plus, 
  Calendar, 
  Tag, 
  Power, 
  PowerOff, 
  Eye,
  Edit,
  History,
  Activity,
  AlertCircle
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

interface AgentVersion {
  id: string
  agentName: 'HR' | 'OPS'
  version: string
  displayName: string
  changelogText: string
  releaseDate: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

const createVersionSchema = z.object({
  agentName: z.enum(['HR', 'OPS'], {
    message: 'Please select an agent type'
  }),
  version: z.string()
    .min(1, 'Version is required')
    .regex(/^\d+\.\d+\.\d+$/, 'Version must be in semantic format (e.g., 1.2.0)'),
  displayName: z.string()
    .min(3, 'Display name must be at least 3 characters')
    .max(100, 'Display name must be less than 100 characters'),
  changelogText: z.string()
    .min(10, 'Changelog must be at least 10 characters')
    .max(10000, 'Changelog must be less than 10,000 characters'),
  releaseDate: z.string()
    .min(1, 'Release date is required')
})

type CreateVersionForm = z.infer<typeof createVersionSchema>

const AgentVersionManagement: React.FC = () => {
  const [versions, setVersions] = useState<AgentVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedAgent, setSelectedAgent] = useState<'ALL' | 'HR' | 'OPS'>('ALL')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [viewingChangelog, setViewingChangelog] = useState<AgentVersion | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const form = useForm<CreateVersionForm>({
    resolver: zodResolver(createVersionSchema),
    defaultValues: {
      agentName: 'HR',
      version: '',
      displayName: '',
      changelogText: '',
      releaseDate: new Date().toISOString().split('T')[0] // Today's date
    }
  })

  useEffect(() => {
    fetchVersions()
  }, [])

  const fetchVersions = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const token = localStorage.getItem('authToken')
      console.log('Token exists:', !!token)
      console.log('Token preview:', token ? `${token.substring(0, 20)}...` : 'null')
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.')
      }
      
      const response = await fetch('/api/agents/versions', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.')
        }
        throw new Error(`Failed to fetch versions: ${response.statusText}`)
      }
      
      const data = await response.json()
      console.log('API response:', data)
      setVersions(data.data || [])
    } catch (err) {
      console.error('Error fetching agent versions:', err)
      setError(err instanceof Error ? err.message : 'Failed to load versions')
      toast.error(err instanceof Error ? err.message : 'Failed to load agent versions')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateVersion = async (data: CreateVersionForm) => {
    try {
      const response = await fetch('/api/agents/versions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to create version')
      }

      const result = await response.json()
      toast.success(`Version ${result.version.version} created successfully`)
      
      // Refresh the list
      await fetchVersions()
      
      // Close modal and reset form
      setIsCreateModalOpen(false)
      form.reset()
    } catch (err) {
      console.error('Error creating version:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to create version')
    }
  }

  const handleActivateVersion = async (versionId: string, agentName: string, version: string) => {
    try {
      setActionLoading(versionId)
      
      const response = await fetch(`/api/agents/versions/${versionId}/activate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to activate version')
      }

      toast.success(`${agentName} Agent v${version} activated successfully`)
      
      // Refresh the list
      await fetchVersions()
    } catch (err) {
      console.error('Error activating version:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to activate version')
    } finally {
      setActionLoading(null)
    }
  }

  const filteredVersions = versions.filter(version => {
    if (selectedAgent === 'ALL') return true
    return version.agentName === selectedAgent
  })

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getAgentBadgeColor = (agentName: string) => {
    return agentName === 'HR' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">Agent Version Management</h3>
            <p className="text-muted-foreground">Loading versions...</p>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-muted/50 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">Agent Version Management</h3>
            <p className="text-muted-foreground">Manage agent versions and changelogs</p>
          </div>
        </div>
        <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <span className="text-destructive">{error}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Agent Version Management</h3>
          <p className="text-muted-foreground">
            Create, manage, and activate different versions of HR and Operations agents
          </p>
        </div>
        
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Create Version
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Agent Version</DialogTitle>
              <DialogDescription>
                Create a new version with changelog for an agent
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={form.handleSubmit(handleCreateVersion)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="agentName">Agent Type</Label>
                  <Select 
                    value={form.watch('agentName')} 
                    onValueChange={(value) => form.setValue('agentName', value as 'HR' | 'OPS')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select agent type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HR">HR Agent</SelectItem>
                      <SelectItem value="OPS">Operations Agent</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.formState.errors.agentName && (
                    <p className="text-sm text-destructive mt-1">
                      {form.formState.errors.agentName.message}
                    </p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="version">Version</Label>
                  <Input
                    {...form.register('version')}
                    placeholder="e.g., 2.1.0"
                  />
                  {form.formState.errors.version && (
                    <p className="text-sm text-destructive mt-1">
                      {form.formState.errors.version.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  {...form.register('displayName')}
                  placeholder="e.g., Enhanced HR Agent with Spanish Support"
                />
                {form.formState.errors.displayName && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.displayName.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="releaseDate">Release Date</Label>
                <Input
                  {...form.register('releaseDate')}
                  type="date"
                />
                {form.formState.errors.releaseDate && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.releaseDate.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="changelogText">Changelog</Label>
                <Textarea
                  {...form.register('changelogText')}
                  placeholder="## New Features&#10;- Added Spanish translation support&#10;- Enhanced document processing&#10;&#10;## Bug Fixes&#10;- Fixed search accuracy issues"
                  className="min-h-[200px] font-mono text-sm"
                />
                {form.formState.errors.changelogText && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.changelogText.message}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? 'Creating...' : 'Create Version'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {['ALL', 'HR', 'OPS'].map((filter) => (
          <Button
            key={filter}
            variant={selectedAgent === filter ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedAgent(filter as typeof selectedAgent)}
            className="flex items-center gap-2"
          >
            {filter === 'ALL' ? (
              <Activity className="h-4 w-4" />
            ) : (
              <Bot className="h-4 w-4" />
            )}
            {filter === 'ALL' ? 'All Agents' : `${filter} Agent`}
          </Button>
        ))}
      </div>

      {/* Version List */}
      <div className="space-y-4">
        {filteredVersions.length === 0 ? (
          <Card>
            <CardContent className="flex items-center justify-center py-8">
              <div className="text-center">
                <Bot className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">
                  No versions found for {selectedAgent === 'ALL' ? 'any agent' : `${selectedAgent} agent`}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredVersions.map((version) => (
            <Card key={version.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={getAgentBadgeColor(version.agentName)}>
                      {version.agentName} Agent
                    </Badge>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        v{version.version}
                        {version.isActive && (
                          <Badge variant="default" className="text-xs">
                            Active
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>{version.displayName}</CardDescription>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setViewingChangelog(version)}
                      className="flex items-center gap-2"
                    >
                      <Eye className="h-4 w-4" />
                      View Changelog
                    </Button>
                    
                    {!version.isActive && (
                      <Button
                        size="sm"
                        onClick={() => handleActivateVersion(version.id, version.agentName, version.version)}
                        disabled={actionLoading === version.id}
                        className="flex items-center gap-2"
                      >
                        <Power className="h-4 w-4" />
                        {actionLoading === version.id ? 'Activating...' : 'Activate'}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Released {formatDate(version.releaseDate)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Tag className="h-4 w-4" />
                      Created {formatDate(version.createdAt)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Changelog Modal */}
      <Dialog open={!!viewingChangelog} onOpenChange={() => setViewingChangelog(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              {viewingChangelog?.agentName} Agent v{viewingChangelog?.version} Changelog
            </DialogTitle>
            <DialogDescription>
              {viewingChangelog?.displayName} • Released {viewingChangelog && formatDate(viewingChangelog.releaseDate)}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh] mt-4">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              {viewingChangelog?.changelogText ? (
                <pre className="whitespace-pre-wrap text-sm">
                  {viewingChangelog.changelogText}
                </pre>
              ) : (
                <p className="text-muted-foreground italic">
                  No changelog available for this version.
                </p>
              )}
            </div>
          </ScrollArea>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setViewingChangelog(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AgentVersionManagement